import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  OnInit
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxMaskDirective } from 'ngx-mask';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { DialogModule } from 'primeng/dialog';
import { stateCodenName } from '../../../common/constants/stateCode';
import { CompanyRequest } from '../../models/company-request';
import { CompanyService } from '../../services/company.service';
import { Messages, ValidationPatterns } from '../../../common/constants/messages';
import { finalize, forkJoin } from 'rxjs';
import { InvalidCharsPipe } from '../../../common/Helpers/invalid-chars.pipe';
import { ValidationHelper } from '../../../common/Helpers/validation-helper';
import { GooglePlaceAutocompleteComponent } from '../../../common/components/google-place-autocomplete/google-place-autocomplete.component';

declare const grecaptcha: any;

@Component({
  selector: 'app-voucher-registration',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgxSpinnerModule, NgxMaskDirective, DialogModule, InvalidCharsPipe,GooglePlaceAutocompleteComponent],
  templateUrl: './voucher-registration.component.html',
  styleUrl: './voucher-registration.component.css'
})
export class VoucherRegistrationComponent
  implements OnInit, AfterViewInit {

  signupForm!: FormGroup;
  statecodenname = stateCodenName;
  locListModalDlg = false;
  dialogSummary = '';
  dialogMsg = '';
  isSaving = false;
  captchaToken: string | null = null;
  prefixList: any[] = [];
  suffixList: any[] = [];
  entityTypeList: any[] = [];
  industryList: any[] = [];
  stateList: any[] = [];
  isIndustryOther = false;
  isEntityTypeOther = false;
  readonly ValidationPatterns = ValidationPatterns;
  isPayDayPerx = false;
  isNsc = false;

  constructor(
    private fb: FormBuilder,
    private companyService: CompanyService,
    private http: HttpClient, private ngx: NgxSpinnerService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  /* ================= INIT ================= */

  ngOnInit(): void {
    this.isPayDayPerx =
      this.router.url.includes('paydayperx-register');
    this.isNsc =
      this.router.url.includes('nsc-register');
    this.buildForm();

    this.signupForm.setValidators(this.emailNotSameValidator());
    this.signupForm.updateValueAndValidity();

    this.setupSecondaryContactValidation();
    this.loadDropdowns();
  }

  get businessInfoForm(): FormGroup {
  return this.signupForm.get('businessInfo') as FormGroup;
}

ngAfterViewInit(): void {
  this.renderCaptcha();
}

  formatPhone(event: any, controlPath: string): void {
    //let digits = event.target.value.replace(/\D/g, '').substring(0, 10);
    //const parts = [];

    // if (digits.length > 3) {
    //   parts.push(digits.substring(0, 3));
    //   if (digits.length > 6) {
    //     parts.push(digits.substring(3, 6));
    //     parts.push(digits.substring(6));
    //   } else {
    //     parts.push(digits.substring(3));
    //   }
    // } else {
    //   parts.push(digits);
    // }
    const cleaned = event.target.value.replace(/\D/g, '').substring(0, 10);

    let formatted = cleaned;
    if (cleaned.length > 6) {
      formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length > 3) {
      formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }

    event.target.value = formatted;
    this.signupForm.get(controlPath)?.setValue(cleaned);
  }

  /* ================= CAPTCHA (FIXED) ================= */

  renderCaptcha(): void {
    const interval = setInterval(() => {
      if (typeof grecaptcha !== 'undefined' && grecaptcha.render) {
        clearInterval(interval);

        grecaptcha.render('recaptcha-container', {
          sitekey: '6LeKNuIUAAAAAPOrmf3IVQt3BmLsABxIQW3JwWXm',
          callback: (token: string) => {
            this.captchaToken = token;
            this.signupForm.get('captcha')?.setValue(token);
            this.signupForm.get('captcha')?.markAsTouched();
          },
          'expired-callback': () => {
            this.captchaToken = null;
            this.signupForm.get('captcha')?.setValue(null);
          }
        });
      }
    }, 300);
  }

  /* ================= FORM ================= */

  buildForm(): void {
    const namePattern = new RegExp(
      ValidationPatterns.NAME,
      "u"
    );

    this.signupForm = this.fb.group({
      businessInfo: this.fb.group({
        businessName: [
          '',
          [
            Validators.required,
            Validators.maxLength(100),
            Validators.pattern(ValidationPatterns.BUSINESS)
          ]
        ],

        /* === Industry fields === */
        industry: ['', Validators.required],
        otherIndustry: [
          '',
          [
            Validators.maxLength(100),
            Validators.pattern(ValidationPatterns.BUSINESS)
          ]
        ],

        /* === Entity Type fields === */
        entityType: [''],
        otherEntityType: [
          '',
          [
            Validators.maxLength(100),
            Validators.pattern(ValidationPatterns.BUSINESS)
          ]
        ],
        employees: ['',
          Validators.pattern(ValidationPatterns.EMPLOYEES)],
        address1: [
          '',
          [
            Validators.required,
            Validators.pattern(ValidationPatterns.ADDRESS)
          ]
        ],

        address2: [
          '',
          [
            Validators.pattern(ValidationPatterns.ADDRESS)
          ]
        ],

        city: ['', [Validators.required, Validators.pattern(ValidationPatterns.CITY)]],
        state: ['', Validators.required],
        zip: [
          '',
          [
            Validators.required,
            Validators.pattern(ValidationPatterns.ZIP)
          ]
        ],
        phone: [
          '',
          [Validators.required, Validators.pattern(ValidationPatterns.PHONE)]
        ],
        companyFax: ['', [Validators.pattern(ValidationPatterns.PHONE), Validators.maxLength(10)]],
        email: ['', [Validators.required, Validators.email]],

        taxExempt: ['', Validators.required],
        employerIdentificationNumber: ['', Validators.pattern(ValidationPatterns.EIN)]
      }),

      primaryContact: this.fb.group({
        prefix: [''],
        firstName: ['', [Validators.required, Validators.pattern(namePattern)]],
        lastName: ['', [Validators.required, Validators.pattern(namePattern)]],
        suffix: [''],
        jobTitle: ['', [Validators.pattern(new RegExp(
          ValidationPatterns.JOB, "u"))]],
        primaryphone: [
          '',
          [Validators.required, Validators.pattern(ValidationPatterns.PHONE)]
        ],
        primaryphoneExt: [
          '',
          [
            Validators.maxLength(10),
            Validators.pattern(ValidationPatterns.PHONE)
          ]
        ],
        fax: ['', Validators.pattern(ValidationPatterns.PHONE)],
        email: [
          '',
          [
            Validators.required,
            Validators.email
          ]
        ]
      }),

      secondaryContact: this.fb.group({
        prefix: [''],
        firstName: ['', Validators.pattern(namePattern)],
        lastName: ['', Validators.pattern(namePattern)],
        suffix: [''],
        jobTitle: ['', [Validators.pattern(new RegExp(
          ValidationPatterns.JOB, "u"))]],
        secondaryphone: ['', Validators.pattern(ValidationPatterns.PHONE)],
        secondaryphoneExt: [
          '',
          [
            Validators.maxLength(10),
            Validators.pattern(ValidationPatterns.PHONE)
          ]
        ],
        secondaryFax: ['', Validators.pattern(ValidationPatterns.PHONE)],
        secondaryEmail: [
          '',
          Validators.email
        ]
      }),

      /* ADD THIS */
      captcha: ['', Validators.required]

    });
  }

  setupSecondaryContactValidation() {

    const secondary = this.signupForm.get('secondaryContact') as FormGroup;

    secondary.valueChanges.subscribe(() => {

      const firstName = secondary.get('firstName')?.value?.trim();
      const lastName = secondary.get('lastName')?.value?.trim();
      const email = secondary.get('secondaryEmail')?.value?.trim();

      const anyFilled = firstName || lastName || email;

      if (anyFilled) {

        secondary.get('firstName')?.setValidators([Validators.required]);
        secondary.get('lastName')?.setValidators([Validators.required]);
        secondary.get('secondaryEmail')?.setValidators([Validators.required, Validators.email]);

      } else {

        secondary.get('firstName')?.clearValidators();
        secondary.get('lastName')?.clearValidators();
        secondary.get('secondaryEmail')?.setValidators([Validators.email]);
      }

      secondary.get('firstName')?.updateValueAndValidity({ emitEvent: false });
      secondary.get('lastName')?.updateValueAndValidity({ emitEvent: false });
      secondary.get('secondaryEmail')?.updateValueAndValidity({ emitEvent: false });

    });
  }

  emailNotSameValidator(): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {

      const form = control as FormGroup;

      const primaryEmail =
        form.get('primaryContact.email')?.value?.trim()?.toLowerCase();

      const secondaryEmailControl =
        form.get('secondaryContact.secondaryEmail');

      const secondaryEmail =
        secondaryEmailControl?.value?.trim()?.toLowerCase();

      if (primaryEmail && secondaryEmail && primaryEmail === secondaryEmail) {
        secondaryEmailControl?.setErrors({
          ...secondaryEmailControl.errors,
          emailMatch: true
        });
      } else {
        if (secondaryEmailControl?.hasError('emailMatch')) {
          const errors = { ...secondaryEmailControl.errors };
          delete errors['emailMatch'];
          secondaryEmailControl.setErrors(
            Object.keys(errors).length ? errors : null
          );
        }
      }

      return null;
    };
  }


  onCaptchaResolved(token: string) {
    this.signupForm.get('captcha')?.setValue(token);
  }


  allowNumbersOnly(event: Event, controlPath: string): void {
    ValidationHelper.allowNumbersOnly(
      event,
      this.signupForm,
      controlPath,
      10
    );
  }

  onInput(event: any) {
    const input = event.target;

    // Keep digits only
    let value = input.value.replace(/\D/g, '');

    // Limit to 6 digits
    value = value.slice(0, 6);

    input.value = value;
    this.signupForm.get('businessInfo.employees')?.setValue(value, { emitEvent: false });
  }


  /* ================= SUBMIT ================= */

  submit(): void {

    // Force captcha to touched so error shows
    this.signupForm.get('captcha')?.markAsTouched();

    if (!this.captchaToken) {
      this.dialogSummary = Messages.ValidationTitle;
      this.dialogMsg = Messages.CaptchaValidationMessage;
      this.locListModalDlg = true;
      return;
    }

    if (this.signupForm.invalid) {
      this.signupForm.markAllAsTouched();

      this.dialogSummary = Messages.ValidationTitle;
      this.dialogMsg = Messages.ValidationMessage;
      this.locListModalDlg = true;
      return;
    }

    this.registerCompany();
  }

  cancel(): void {
    this.router.navigate(['']);
  }


  onIndustryChange(value: string) {
    const selected = this.industryList.find(i => i.industryPk == value);
    this.isIndustryOther =
      selected?.industryName?.toLowerCase() === 'other';

    const otherIndustryControl =
      this.signupForm.get('businessInfo.otherIndustry');

    if (this.isIndustryOther) {
      otherIndustryControl?.setValidators([
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(ValidationPatterns.BUSINESS)
      ]);
      otherIndustryControl?.reset('');
    } else {
      otherIndustryControl?.clearValidators();
      otherIndustryControl?.reset();
    }

    otherIndustryControl?.updateValueAndValidity();
  }

  onEntityTypeChange(value: string) {
    const selected = this.entityTypeList.find(e => e.entityTypePk == value);
    this.isEntityTypeOther =
      selected?.entityTypeName?.toLowerCase() === 'other';

    const otherEntityTypeControl =
      this.signupForm.get('businessInfo.otherEntityType');

    if (this.isEntityTypeOther) {
      otherEntityTypeControl?.setValidators([
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(ValidationPatterns.BUSINESS)
      ]);
      otherEntityTypeControl?.reset('');
    } else {
      otherEntityTypeControl?.clearValidators();
      otherEntityTypeControl?.reset();
    }

    otherEntityTypeControl?.updateValueAndValidity();
  }

  /* ================= API ================= */

  registerCompany(): void {
    this.ngx.show();
    this.isSaving = true;

    const businessInfo = this.signupForm.get('businessInfo') as FormGroup;
    const primaryContact = this.signupForm.get('primaryContact') as FormGroup;
    const secondaryContact = this.signupForm.get('secondaryContact') as FormGroup;

    const formValue = this.signupForm.value;
    const business = formValue.businessInfo;

    let industryPk: number | null = business.industry || null;
    let entityTypePk: number | null = business.entityType || null;

    let industryOther: string | null = null;
    let entityTypeOther: string | null = null;

    /* ================= INDUSTRY ================= */
    if (this.isIndustryOther) {
      const otherIndustry = this.industryList.find(
        i => i.industryName?.toLowerCase() === 'other'
      );

      if (otherIndustry) {
        industryPk = otherIndustry.industryPk;
        industryOther = business.otherIndustry || null;
      }
    }

    /* ================= ENTITY TYPE ================= */
    if (this.isEntityTypeOther) {
      const otherEntityType = this.entityTypeList.find(
        e => e.entityTypeName?.toLowerCase() === 'other'
      );

      if (otherEntityType) {
        entityTypePk = otherEntityType.entityTypePk;
        entityTypeOther = business.otherEntityType || null;
      }
    }

    const payload: CompanyRequest = {
      companyId: null,
      companyName: businessInfo.value.businessName.trim(),
      industry: businessInfo.value.industry || null,
      industryOther: industryOther,
      entityType: businessInfo.value.entityType || null,
      entityTypeOther: entityTypeOther,
      numberOfEmployees: businessInfo.value.employees || null,
      address1: businessInfo.value.address1.trim(),
      address2: businessInfo.value.address2.trim(),
      city: businessInfo.value.city.trim(),
      state: businessInfo.value.state.trim() || null,
      zip: businessInfo.value.zip,
      companyPhone: businessInfo.value.phone,
      companyEmail: businessInfo.value.email,
      companyFax: businessInfo.value.companyFax,
      taxExempt: this.toBoolean(businessInfo.value.taxExempt),
      employerIdentificationNumber: businessInfo.value.employerIdentificationNumber,
      primaryContactPrefix: primaryContact.value.prefix || null,
      primaryContactFirstName: primaryContact.value.firstName.trim(),
      primaryContactLastName: primaryContact.value.lastName.trim(),
      primaryContactSuffix: primaryContact.value.suffix || null,
      primaryContactJobTitle: primaryContact.value.jobTitle.trim(),
      primaryContactPhone: primaryContact.value.primaryphone,
      primaryContactPhoneExtension: primaryContact.value.primaryphoneExt,
      primaryContactFax: primaryContact.value.fax,
      primaryContactEmail: primaryContact.value.email,
      secondaryContactPrefix: secondaryContact.value.prefix || null,
      secondaryContactFirstName: secondaryContact.value.firstName.trim(),
      secondaryContactLastName: secondaryContact.value.lastName.trim(),
      secondaryContactSuffix: secondaryContact.value.suffix || null,
      secondaryContactJobTitle: secondaryContact.value.jobTitle.trim(),
      secondaryContactPhone: secondaryContact.value.secondaryphone,
      secondaryContactPhoneExtension: secondaryContact.value.secondaryphoneExt,
      secondaryContactFax: secondaryContact.value.secondaryFax,
      secondaryContactEmail: secondaryContact.value.secondaryEmail || null,
      isPayDayPerx : this.isPayDayPerx,
      isNsc : this.isNsc
    };

    this.companyService.createCompany(payload).subscribe({
      next: (res) => {
        this.ngx.hide();
        this.isSaving = false;
        if (res && res.isSuccess) {
          this.dialogSummary = Messages.RegistrationSuccessTitle;
          this.dialogMsg = Messages.VoucherRegistrationSuccessful;
          this.locListModalDlg = true;
          this.isEntityTypeOther = false;
          this.isIndustryOther = false;
          this.signupForm.get('captcha')?.reset();
          this.captchaToken = null;
          grecaptcha.reset();
        }
        else {
          this.ngx.hide();
          this.isSaving = false;
          this.dialogSummary = "Registration Error";
          this.dialogMsg = res.errorMessage;
          this.locListModalDlg = true;
        }
      },
      error: (err) => {
        this.ngx.hide();
        this.isSaving = false;
        this.dialogSummary = Messages.ErrorTitle;
        this.dialogMsg = Messages.SaveFailedMessage;
        this.locListModalDlg = true;
      }
    });
  }

  okClickedV2() {
    this.locListModalDlg = false;
    if (this.dialogSummary === Messages.RegistrationSuccessTitle) {
      this.router.navigate(['']); // landing route
    }
  }

  /* ================= HELPERS ================= */

  isFieldInvalid(path: string): boolean {
    const control = this.signupForm.get(path);
    return !!(control && control.invalid && control.touched);
  }

  displayFieldCss(path: string) {
    return { 'has-error': this.isFieldInvalid(path) };
  }

  toBoolean(value: string | boolean | null): boolean {
    return value === true || value === 'true';
  }

  getStateCode(stateName: string): string {
    const match = this.statecodenname.find(
      s => s.stateName.toLowerCase() === stateName.toLowerCase()
    );
    return match ? match.stateCode : '';
  }

  /* ================= DROPDOWNS ================= */

  loadDropdowns(): void {
    this.ngx.show()
    forkJoin({
      prefixes: this.companyService.getAllPrefixes(),
      suffixes: this.companyService.getAllSuffixes(),
      entityTypes: this.companyService.getAllEntityTypes(),
      industries: this.companyService.getAllIndustries(),
      states: this.companyService.getAllStates()
    })
      .pipe(
        finalize(() => this.ngx.hide())
      )
      .subscribe({
        next: (res) => {
          this.prefixList = res.prefixes;
          this.suffixList = res.suffixes;
          this.entityTypeList = res.entityTypes;
          this.industryList = res.industries;
          this.stateList = res.states;
        },
        error: (err) => {
          this.dialogSummary = Messages.ErrorTitle;
          this.dialogMsg = Messages.ErrorInAPICall;
          this.locListModalDlg = true;
        }
      });
  }
}

